import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { useEffectOnce } from 'react-use'
import { ISelectOption } from 'components/forms/Selects'
import { Box, Typography } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import { getCarrierDetails } from 'utils/actions/carrier'
import { selectCarrierDetails } from 'utils/reducers/carrier'
import { useGetCarrierActiveShipments } from 'apps/carrier/apiTypes/activeShipments'
import getDrivers from 'apps/carrier/utils/api/getDrivers'
import listByLaneSchema, { IByLaneKey } from 'apps/carrier/utils/laneShipmentSchema'
import { useCallApi } from 'apps/marketplace/utils/hooks/useCallApi'
import formatPhone from 'utils/formatPhone'
import { DISPLAY } from 'constants/display'
import { LaneGroup } from './LaneGroup'
import LoadingLaneGroup from './LoadingUI'
import { ShipmentRow } from './ShipmentRow'
import '../../scss/edit.scss'

// Page for 'carrier/edit/:carrierId'
const ActiveShipments: FC = () => {
  const dispatch = useDispatch()
  const [driverList, setdriverList] = useState<ISelectOption<number>[]>([{ label: '', value: 0 }])
  const { id } = useParams<{ id: string }>()
  const carrierId = Number(id)
  const { data, loading } = useGetCarrierActiveShipments({ carrierId })
  const { load: fetchDriversList } = useCallApi({ apiCall: getDrivers })

  const [lanes, setLanes] = useState<IByLaneKey[]>([])
  const carrierDetails = useSelector(selectCarrierDetails)

  const { dispatchers } = data || {}

  // After shipment list is loaded, normalize data to categorize by lane
  useEffect(() => {
    if (!loading) {
      const laneList = listByLaneSchema(data)

      setLanes(laneList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  useEffectOnce(() => {
    // Load carrier details if it's not available
    if (!carrierDetails) {
      dispatch(getCarrierDetails(id))
    }

    // Load carrier's list of drivers
    fetchDriversList({ carrierId }).then(response => {
      const optionMapped = response.users.map(user => ({
        label: `${user.firstName} ${user.lastName || ''}`,
        value: Number(user.id),
      }))

      setdriverList(optionMapped)
    })
  })

  return (
    <Box className='edit-container' mt={6}>
      <Box padding={3}>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <div>
            <Box mb={2}>
              <Typography variant='subtitle1' color='primary' component='div'>
                {carrierDetails ? carrierDetails.legalName.toUpperCase() : <Skeleton />}
              </Typography>
            </Box>
            <Typography variant='h3'>Update Active Shipments</Typography>
          </div>
        </Box>
        <Box marginY={2} padding={3} display='flex' className='carrier-info'>
          <Box width={1 / 3}>
            <Typography variant='subtitle2' color='textSecondary'>
              Dispatchers
            </Typography>
            <Box display='flex' mt={1} flexDirection='column'>
              {loading && <Skeleton />}
              {dispatchers?.map(dispatcher => (
                <div key={dispatcher.id}>
                  <a
                    href={`${__ROOT__}/users/${dispatcher.id}`}
                    target='_blank'
                    rel='noopener noreferrer'>
                    {dispatcher.firstName} {dispatcher.lastName}
                  </a>{' '}
                  | <a href={`tel:+${dispatcher.phone}`}>{formatPhone(dispatcher.phone)} | </a>
                  <a href={`mailto:${dispatcher.email}`}>{dispatcher.email}</a>
                </div>
              ))}
            </Box>
          </Box>
          <Box width={1 / 3} marginX={3}>
            <Typography variant='subtitle2' color='textSecondary'>
              Instructions for Contacting Carrier
            </Typography>
            <Box mt={1}>
              {carrierDetails ? (
                carrierDetails.carrierSpecificInfo || DISPLAY.emptyDash
              ) : (
                <>
                  <Skeleton width='100%' />
                  <Skeleton width='100%' />
                  <Skeleton width='100%' />
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Loading State */}
      {loading && <LoadingLaneGroup />}

      {/* Empty State */}
      {!lanes.length && !loading && (
        <Box px={3} className='shipments-empty-state'>
          <Typography variant='caption'>No Active Shipments</Typography>
        </Box>
      )}

      {/* Display State */}
      {lanes.map(({ laneFrom, laneTo, shipments }) => (
        <LaneGroup key={`${laneTo}-${laneFrom}`} laneTo={laneTo} laneFrom={laneFrom}>
          {shipments.map(shipment => (
            <ShipmentRow key={shipment.id} shipment={shipment} driverList={driverList} />
          ))}
        </LaneGroup>
      ))}
    </Box>
  )
}

export default ActiveShipments
